import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Article, Sentence, SentenceVerb } from '@/types/article';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  ChevronLeft,
  ChevronRight,
  X,
  BookOpen,
  Edit2,
  Save,
  Plus,
  Trash2,
  Menu,
  Check,
  ArrowUp,
  ArrowDown,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface ArticleEditorProps {
  article: Article;
  onSave: (
    updatedArticle: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>
  ) => void;
  onClose: () => void;
}

const ArticleEditor = ({ article, onSave, onClose }: ArticleEditorProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sentences, setSentences] = useState<Sentence[]>(article.sentences);
  const [title] = useState(article.title);
  const [description] = useState(article.description || '');

  // Editing states for current sentence
  const [editingGerman, setEditingGerman] = useState(false);
  const [editingEnglish, setEditingEnglish] = useState(false);
  const [editingVietnamese, setEditingVietnamese] = useState(false);
  const [editingVerbIndex, setEditingVerbIndex] = useState<number | null>(null);

  // Navigation menu state
  const [showNavigation, setShowNavigation] = useState(false);

  const currentSentence = sentences[currentIndex];

  const resetEditingStates = useCallback(() => {
    setEditingGerman(false);
    setEditingEnglish(false);
    setEditingVietnamese(false);
    setEditingVerbIndex(null);
  }, []);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetEditingStates();
    }
  }, [currentIndex, resetEditingStates]);

  const goToNext = useCallback(() => {
    if (currentIndex < sentences.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetEditingStates();
    }
  }, [currentIndex, sentences.length, resetEditingStates]);

  const goToSentence = (index: number) => {
    setCurrentIndex(index);
    setShowNavigation(false);
    resetEditingStates();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        editingGerman ||
        editingEnglish ||
        editingVietnamese ||
        editingVerbIndex !== null
      )
        return;

      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    currentIndex,
    editingGerman,
    editingEnglish,
    editingVietnamese,
    editingVerbIndex,
    goToNext,
    goToPrevious,
    onClose,
  ]);

  const updateSentence = (
    field: 'german' | 'english' | 'vietnamese',
    value: string
  ) => {
    setSentences(prev =>
      prev.map((s, i) => (i === currentIndex ? { ...s, [field]: value } : s))
    );
  };

  const updateVerb = (
    verbIndex: number,
    field: keyof SentenceVerb,
    value: string
  ) => {
    setSentences(prev =>
      prev.map((s, i) =>
        i === currentIndex
          ? {
              ...s,
              verbs:
                s.verbs?.map((v, vi) =>
                  vi === verbIndex ? { ...v, [field]: value } : v
                ) || [],
            }
          : s
      )
    );
  };

  const addVerb = () => {
    const newVerb: SentenceVerb = {
      german: '',
      english: '',
      vietnamese: '',
    };

    setSentences(prev =>
      prev.map((s, i) =>
        i === currentIndex
          ? {
              ...s,
              verbs: [...(s.verbs || []), newVerb],
            }
          : s
      )
    );

    setEditingVerbIndex(currentSentence.verbs?.length || 0);
  };

  const deleteVerb = (verbIndex: number) => {
    setSentences(prev =>
      prev.map((s, i) =>
        i === currentIndex
          ? {
              ...s,
              verbs: s.verbs?.filter((_, vi) => vi !== verbIndex) || [],
            }
          : s
      )
    );
  };

  const moveSentence = (direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= sentences.length) return;

    setSentences(prev => {
      const newSentences = [...prev];
      [newSentences[currentIndex], newSentences[newIndex]] = [
        newSentences[newIndex],
        newSentences[currentIndex],
      ];
      return newSentences.map((s, i) => ({ ...s, order: i + 1 }));
    });

    // Update current index to follow the moved sentence
    setCurrentIndex(newIndex);

    toast({
      title: t('articleEditor.sentenceMoved'),
      description: t('articleEditor.sentenceMovedDesc', {
        direction: direction === 'up' ? t('ui.up') : t('ui.down'),
      }),
    });
  };

  const handleSave = () => {
    const updatedArticle = {
      title: title.trim(),
      description: description.trim() || undefined,
      sentences: sentences,
      imageUrl: article.imageUrl,
      imagePublicId: article.imagePublicId,
      originalLink: article.originalLink,
    };

    onSave(updatedArticle);
    toast({
      title: t('articleEditor.articleSaved'),
      description: t('articleEditor.articleSavedDesc'),
    });
  };

  const renderVerbsSection = () => {
    // if (!hasVerbs && !editingVerbIndex) return null;

    const verbs = currentSentence.verbs || [];
    const verbCount = verbs.length;

    return (
      <Card className="bg-gradient-card shadow-card border-learning-muted/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-learning-primary" />
              Wörter-Buch
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {verbCount}{' '}
                {verbCount === 1
                  ? t('articleEditor.word')
                  : t('articleEditor.words')}
              </Badge>
              <Button size="sm" variant="outline" onClick={addVerb}>
                <Plus className="h-3 w-3 mr-1" />
                {t('actions.add')}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {verbs.map((verb, index) => (
            <div
              key={index}
              className="group p-4 bg-white/50 rounded-lg border border-learning-muted/40 hover:border-learning-primary/40 transition-colors duration-200 space-y-3"
            >
              {editingVerbIndex === index ? (
                <div className="space-y-3">
                  <Input
                    value={verb.german}
                    onChange={e => updateVerb(index, 'german', e.target.value)}
                    placeholder={t('placeholders.germanWord')}
                    className="font-semibold text-lg"
                  />
                  <Input
                    value={verb.english}
                    onChange={e => updateVerb(index, 'english', e.target.value)}
                    placeholder={t('placeholders.englishTranslation')}
                  />
                  <Input
                    value={verb.vietnamese}
                    onChange={e =>
                      updateVerb(index, 'vietnamese', e.target.value)
                    }
                    placeholder={t('placeholders.vietnameseTranslation')}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setEditingVerbIndex(null)}>
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteVerb(index)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div className="font-semibold text-foreground text-lg group-hover:text-learning-primary transition-colors">
                      {verb.german}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingVerbIndex(index)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <Badge
                        variant="secondary"
                        className="bg-learning-secondary/20 text-learning-secondary text-xs mt-0.5 flex-shrink-0"
                      >
                        🇬🇧 EN
                      </Badge>
                      <span className="text-sm text-muted-foreground leading-relaxed">
                        {verb.english}
                      </span>
                    </div>

                    <div className="flex items-start gap-3">
                      <Badge
                        variant="secondary"
                        className="bg-learning-accent/20 text-learning-accent text-xs mt-0.5 flex-shrink-0"
                      >
                        🇻🇳 VI
                      </Badge>
                      <span className="text-sm text-muted-foreground leading-relaxed">
                        {verb.vietnamese}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderNavigationMenu = () => (
    <Sheet open={showNavigation} onOpenChange={setShowNavigation}>
      <SheetContent side="left" className="w-80">
        <SheetHeader>
          <SheetTitle>{t('articleEditor.navigateToSentence')}</SheetTitle>
          <SheetDescription>
            {t('articleEditor.navigateDescription')}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
          {sentences.map((sentence, index) => (
            <Button
              key={index}
              variant={index === currentIndex ? 'default' : 'ghost'}
              onClick={() => goToSentence(index)}
              className="w-full justify-start text-left h-auto p-3"
            >
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">
                  {t('translationEdit.sentence')} {index + 1}
                  {index === 0 && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      {t('translationEdit.title')}
                    </Badge>
                  )}
                </div>
                <div className="text-sm font-medium line-clamp-2">
                  {sentence.german}
                </div>
              </div>
            </Button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-hero p-4 text-primary-foreground">
        <div className="container max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-primary-foreground hover:bg-white/20"
            >
              <X className="h-4 w-4 mr-2" />
              {t('actions.close')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNavigation(true)}
              className="text-primary-foreground hover:bg-white/20"
            >
              <Menu className="h-4 w-4 mr-2" />
              {t('articleEditor.navigate')}
            </Button>
            <div className="text-sm opacity-90">
              {t('articleEditor.sentenceCount', {
                current: currentIndex + 1,
                total: sentences.length,
              })}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {article.originalLink && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  window.open(
                    article.originalLink,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
                className="text-primary-foreground hover:bg-white/20"
                title={t('articleCard.viewOriginalSource')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('articleEditor.source')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              className="text-primary-foreground hover:bg-white/20"
            >
              <Save className="h-4 w-4 mr-2" />
              {t('articleEditor.saveChanges')}
            </Button>
          </div>
        </div>
      </div>

      {/* Article Title and Description */}
      {/* <div className="bg-muted/50 p-6 border-b">
        <div className="container max-w-4xl mx-auto space-y-4">
          {editingTitle ? (
            <div className="flex gap-2">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-xl font-bold"
                placeholder={t('placeholders.articleTitle')}
              />
              <Button onClick={() => setEditingTitle(false)}>
                <Check className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h1 className="text-2xl font-bold text-foreground">{title}</h1>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingTitle(true)}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {editingDescription ? (
            <div className="flex gap-2">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('placeholders.articleDescription')}
                className="min-h-[60px]"
              />
              <Button onClick={() => setEditingDescription(false)}>
                <Check className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-start gap-2 group">
              <p className="text-muted-foreground">{description || 'Click to add description'}</p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingDescription(true)}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div> */}

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="container max-w-7xl mx-auto p-6">
          {/* Main Layout - Responsive Grid */}
          <div
            className={cn(
              'grid grid-cols-1 gap-8 min-h-full',
              'xl:grid-cols-3'
            )}
          >
            {/* Sentences Column */}
            <div className={cn('space-y-8 animate-fade-in', 'xl:col-span-2')}>
              {/* Sentence controls */}
              <div className="flex justify-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moveSentence('up')}
                  disabled={currentIndex === 0}
                >
                  <ArrowUp className="h-3 w-3 mr-1" />
                  {t('articleEditor.moveUp')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => moveSentence('down')}
                  disabled={currentIndex === sentences.length - 1}
                >
                  <ArrowDown className="h-3 w-3 mr-1" />
                  {t('articleEditor.moveDown')}
                </Button>
              </div>

              {/* German */}
              <div className="text-center group">
                <div className="text-sm font-medium text-learning-primary mb-3">
                  {t('translationEdit.germanOriginal')}
                </div>
                {editingGerman ? (
                  <div className="flex gap-2 justify-center">
                    <Textarea
                      value={currentSentence.german}
                      onChange={e => updateSentence('german', e.target.value)}
                      className="text-2xl md:text-3xl lg:text-4xl font-semibold text-center"
                    />
                    <Button onClick={() => setEditingGerman(false)}>
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground leading-relaxed">
                      {currentSentence.german}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingGerman(true)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* English */}
              <div className="text-center animate-slide-in group">
                <div className="text-sm font-medium text-learning-secondary mb-3">
                  {t('translationEdit.english')}
                </div>
                {editingEnglish ? (
                  <div className="flex gap-2 justify-center">
                    <Textarea
                      value={currentSentence.english}
                      onChange={e => updateSentence('english', e.target.value)}
                      className="text-lg md:text-xl lg:text-2xl text-center"
                    />
                    <Button onClick={() => setEditingEnglish(false)}>
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                      {currentSentence.english}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingEnglish(true)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Vietnamese */}
              <div className="text-center animate-slide-in group">
                <div className="text-sm font-medium text-learning-accent mb-3">
                  {t('translationEdit.vietnamese')}
                </div>
                {editingVietnamese ? (
                  <div className="flex gap-2 justify-center">
                    <Textarea
                      value={currentSentence.vietnamese}
                      onChange={e =>
                        updateSentence('vietnamese', e.target.value)
                      }
                      className="text-lg md:text-xl lg:text-2xl text-center"
                    />
                    <Button onClick={() => setEditingVietnamese(false)}>
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                      {currentSentence.vietnamese}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingVietnamese(true)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Verbs section for mobile/tablet */}
              {<div className="xl:hidden mt-8">{renderVerbsSection()}</div>}
            </div>

            {/* Verbs Sidebar */}
            {
              <div className="hidden xl:block">
                <div className="sticky top-6">{renderVerbsSection()}</div>
              </div>
            }
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-muted p-4">
        <div className="container max-w-4xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            className="bg-white/50 backdrop-blur-sm"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('ui.previous')}
          </Button>

          {/* Progress bar */}
          <div className="flex-1 mx-8">
            <div className="bg-learning-muted rounded-full h-2">
              <div
                className="bg-gradient-hero h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentIndex + 1) / sentences.length) * 100}%`,
                }}
              />
            </div>
          </div>

          <Button
            variant="outline"
            onClick={goToNext}
            disabled={currentIndex === sentences.length - 1}
            className="bg-white/50 backdrop-blur-sm"
          >
            {t('ui.next')}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Navigation Menu */}
      {renderNavigationMenu()}
    </div>
  );
};

export default ArticleEditor;
